import { Box, Button, Input, Link, Spinner, TabPanel, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { Address, useAccount, useContractRead, useContractWrite, useNetwork, usePrepareContractWrite } from "wagmi";
import { useAllowance } from "../../helpers/useAllowance";
import BalanceAction from "./BalanceAction";
import useDebounce from "../../helpers/useDebounce";
import { VaultDetails } from "../../types/vault";
import { formatUnits, parseUnits } from "viem";
import { waitForTransaction } from "@wagmi/core";
import { TokenBalanceDetails } from "../../types/tokens";
import { tsToHours } from "../../helpers/formatters";

export default function DepositTabPanel({
  vaultInfo,
  sourceTokenDetails,
}: {
  vaultInfo: VaultDetails | undefined;
  sourceTokenDetails: TokenBalanceDetails | undefined;
}) {
  const { chain } = useNetwork();
  const amountPerEpochInputRef = useRef<any>(null);
  const numEpochsInputRef = useRef<any>(null);

  const { address } = useAccount();

  const [depositAmount, setDepositAmount] = useState<bigint | undefined>();

  const [amountPerEpoch, setAmountPerEpoch] = useState<bigint | undefined>();
  const debouncedAmountPerEpoch = useDebounce<bigint | undefined>(amountPerEpoch, 500);

  const [numEpochs, setNumEpochs] = useState<bigint | undefined>();
  const debouncedNumEpochs = useDebounce<bigint | undefined>(numEpochs, 500);

  const [showApproveSpendMsgs, setShowApproveSpendMsgs] = useState(false);
  const [showDepositMsgs, setShowDepositMsgs] = useState(false);

  useEffect(() => {
    if (debouncedAmountPerEpoch && debouncedNumEpochs) {
      setDepositAmount(debouncedAmountPerEpoch * debouncedNumEpochs);
    } else {
      setDepositAmount(undefined);
    }
  }, [debouncedAmountPerEpoch, debouncedNumEpochs]);

  const { data: allowance } = useAllowance({
    ownerAddressOrName: address,
    spenderAddressOrName: vaultInfo?.vaultAddress,
    token: vaultInfo?.sourceTokenAddress,
    watch: true,
  });

  const {
    data: previewDepositData,
    error: previewDepositErr,
    isLoading: isLoadingPreviewDepositAmt,
    isSuccess: isSuccessPreviewDepositAmt,
  } = useContractRead({
    address: vaultInfo?.vaultAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "uint256",
            name: "depositAmount",
            type: "uint256",
          },
        ],
        name: "previewDeposit",
        outputs: [
          {
            internalType: "uint256",
            name: "",
            type: "uint256",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
    ],
    functionName: "previewDeposit",
    args: [depositAmount ?? BigInt(0)],
    enabled: depositAmount !== undefined,
  });

  // @ts-ignore
  const { config: approveSpendConfig } = usePrepareContractWrite({
    address: vaultInfo?.sourceTokenAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "address",
            name: "spender",
            type: "address",
          },
          {
            internalType: "uint256",
            name: "amount",
            type: "uint256",
          },
        ],
        name: "approve",
        outputs: [
          {
            internalType: "bool",
            name: "",
            type: "bool",
          },
        ],
        stateMutability: "nonpayable",
        type: "function",
      },
    ],
    functionName: "approve",
    // @ts-ignore
    args: [vaultInfo?.vaultAddress, depositAmount],
    enabled: vaultInfo && depositAmount !== undefined && depositAmount > 0,
  });
  const {
    data: approveSpendRx,
    error: approveSpendErr,
    isError: isApproveSpendError,
    isLoading: isApproveSpendLoading,
    isSuccess: isApproveSpendSuccess,
    writeAsync: approveSpendWrite,
  } = useContractWrite(approveSpendConfig);

  // @ts-ignore
  const { config: depositConfig } = usePrepareContractWrite({
    address: vaultInfo?.vaultAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "uint256",
            name: "amountPerEpoch",
            type: "uint256",
          },
          {
            internalType: "uint256",
            name: "numEpochs",
            type: "uint256",
          },
          {
            internalType: "address",
            name: "receiver",
            type: "address",
          },
        ],
        name: "deposit",
        outputs: [
          {
            internalType: "uint256",
            name: "sharesAmt",
            type: "uint256",
          },
        ],
        stateMutability: "nonpayable",
        type: "function",
      },
    ],
    functionName: "deposit",
    args: [debouncedAmountPerEpoch ?? BigInt(0), debouncedNumEpochs ?? BigInt(0), address as Address],
    enabled: depositAmount !== undefined && allowance !== undefined && depositAmount <= allowance?.value,
  });
  const {
    data: depositRx,
    error: depositErr,
    isError: isDepositError,
    isLoading: isDepositLoading,
    isSuccess: isDepositSuccess,
    writeAsync: depositWrite,
  } = useContractWrite(depositConfig);

  return (
    <TabPanel>
      <BalanceAction
        token={vaultInfo?.sourceTokenAddress}
        tokenName={sourceTokenDetails?.symbol}
        tokenDecimals={Number(sourceTokenDetails?.decimals)}
      />

      {/* amount per epoch */}
      <Box mt="1em">
        <Text fontSize="xs" fontWeight="bold">
          Amount per epoch
        </Text>
        <Input
          display="block"
          type="number"
          size="md"
          step={1}
          min={0}
          minW="8em"
          onChange={(evt) => {
            if (evt.target.value) setAmountPerEpoch(parseUnits(evt.target.value, Number(sourceTokenDetails?.decimals)));
            else setAmountPerEpoch(undefined);
          }}
          placeholder={"10 " + (sourceTokenDetails ? sourceTokenDetails?.symbol : "tokens")}
          disabled={isDepositLoading}
          variant="flushed"
          ref={amountPerEpochInputRef}
        />
      </Box>

      {/* num epochs */}
      <Box mt="1em">
        <Text fontSize="xs" fontWeight="bold">
          Number of epochs {vaultInfo ? "1 epoch = " + tsToHours(vaultInfo.epochLength) : ""}
        </Text>
        <Input
          display="block"
          type="number"
          size="md"
          step={1}
          min={0}
          minW="8em"
          onChange={(evt) => {
            if (evt.target.value) setNumEpochs(BigInt(evt.target.value));
            else setNumEpochs(undefined);
          }}
          placeholder={"30 epochs"}
          disabled={isDepositLoading}
          variant="flushed"
          ref={numEpochsInputRef}
        />
      </Box>

      <Box mt="1em">
        {depositAmount && allowance && (depositAmount > allowance.value || isApproveSpendLoading) ? (
          // approve spending button
          <Button
            display="inline"
            size="sm"
            colorScheme="blue"
            isDisabled={
              // TODO: this is out of sync because of allowance (read next todo)
              // disable if approving
              isApproveSpendLoading ||
              // disable if allowance hasn't been retrieved
              !allowance
            }
            onClick={() => {
              setShowApproveSpendMsgs(true);
              setShowDepositMsgs(false);
              // should not be able to click without depositAmout && allowance
              // if allowance is less than the deposit amount, then approve spending
              if (allowance.value < depositAmount) {
                approveSpendWrite?.()
                  .then((tx) => {
                    // TODO: use this to enable the deposit, instead of relying on the loading flags?
                    // waiting 3 confirmations can help with the allowance getting updated
                    waitForTransaction({ hash: tx.hash, confirmations: 3 }).then((rx) => {});
                  })
                  .catch((err) => {
                    console.log("APPROVE_ERR", err); // TODO: TBD
                  });
              }
            }}
          >
            Approve Spending
            {/* loading msg */}
            {isApproveSpendLoading && <Spinner size="xs" ml="1em" />}
          </Button>
        ) : (
          // deposit button
          <Button
            display="inline"
            size="sm"
            colorScheme="green"
            isDisabled={
              // disable if there's no deposit amount (input not filled or <= 0)
              !(depositAmount && depositAmount > 0) ||
              // disable if depositing
              isDepositLoading ||
              isApproveSpendLoading ||
              // disable if allowance hasn't been retrieved
              !allowance
            }
            onClick={() => {
              setShowApproveSpendMsgs(false);
              setShowDepositMsgs(true);
              // should not be able to click without depositAmout && allowance

              // allowance is enough, just deposit
              depositWrite?.()
                .then((tx) => {
                  waitForTransaction({ hash: tx.hash, confirmations: 1 }).then((rx) => {
                    // clear inputs
                    amountPerEpochInputRef.current.value = "";
                    numEpochsInputRef.current.value = "";
                    setAmountPerEpoch(undefined);
                    setNumEpochs(undefined);
                    setDepositAmount(undefined);
                  });
                })
                .catch((err) => {
                  console.log("DEPOSIT_ERR", err); // TODO: TBD
                });
            }}
          >
            Deposit {sourceTokenDetails?.symbol}
            {/* loading msg */}
            {isDepositLoading && <Spinner size="xs" ml="1em" />}
          </Button>
        )}
      </Box>

      {/* preview deposit msgs */}
      {isLoadingPreviewDepositAmt && <Spinner size="xs" mt="1em" />}
      {previewDepositErr && (
        <Text display="block" mt="1em" fontSize="xs" colorScheme="red">
          Error previewing the deposit!
        </Text>
      )}
      {isSuccessPreviewDepositAmt && depositAmount !== undefined && (
        <Text display="block" mt="1em" fontSize="xs">
          You will receive approx. {formatUnits(previewDepositData as bigint, Number(vaultInfo?.vaultDecimals))}&nbsp;
          {vaultInfo?.vaultSymbol}
        </Text>
      )}

      {/* approval required msg */}
      {depositAmount !== undefined && allowance !== undefined && depositAmount > allowance.value && (
        <Text fontSize="xs" mt="1em" fontStyle="italic">
          Spending the defined amount requires your approval.
        </Text>
      )}

      {/* spending approval success/error msgs */}
      {showApproveSpendMsgs && isApproveSpendSuccess && (
        <Link
          display="block"
          mt="1em"
          href={chain?.blockExplorers?.default.url + "/tx/" + approveSpendRx?.hash}
          isExternal
          fontSize="xs"
          color="teal.500"
        >
          Spending approved, you can now deposit.
        </Link>
      )}
      {showApproveSpendMsgs && isApproveSpendError && (
        <Text display="block" fontSize="xs" mt="1em" color="red.600">
          Spending approval failed! {approveSpendErr?.message.substring(0, 280)}
        </Text>
      )}

      {/* deposit success/error msgs */}
      {showDepositMsgs && isDepositSuccess && depositAmount === undefined && (
        <Link
          display="block"
          mt="1em"
          href={chain?.blockExplorers?.default.url + "/tx/" + depositRx?.hash}
          isExternal
          fontSize="xs"
          color="teal.500"
        >
          Your deposit has been successful!
        </Link>
      )}

      {showDepositMsgs && isDepositError && (
        <Text display="block" mt="1em" fontSize="xs" color="red.600">
          Something's gone wrong with your deposit. {depositErr?.message.substring(0, 280)}
        </Text>
      )}
    </TabPanel>
  );
}
